/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { remoteSessionClientsUnlinkEMA } from "../funcs/remoteSessionClientsUnlinkEMA.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { IdentityChainingPreparation } from "../models/components/identitychainingpreparation.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UnlinkEMARequest,
  UnlinkEMASecurity,
} from "../models/operations/unlinkema.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RemoteSessionClientsUnlinkEMAMutationVariables = {
  request: UnlinkEMARequest;
  security?: UnlinkEMASecurity | undefined;
  options?: RequestOptions;
};

export type RemoteSessionClientsUnlinkEMAMutationData =
  IdentityChainingPreparation;

export type RemoteSessionClientsUnlinkEMAMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * unlinkEMA remoteSessionClients
 *
 * @remarks
 * Explicit, tenant-scoped identity-chaining configuration. Mutations require project-write authorization; reads require project-read authorization. Does not exchange tokens or establish provider verification, trust, consent, or user access.
 */
export function useRemoteSessionClientsUnlinkEMAMutation(
  options?: MutationHookOptions<
    RemoteSessionClientsUnlinkEMAMutationData,
    RemoteSessionClientsUnlinkEMAMutationError,
    RemoteSessionClientsUnlinkEMAMutationVariables
  >,
): UseMutationResult<
  RemoteSessionClientsUnlinkEMAMutationData,
  RemoteSessionClientsUnlinkEMAMutationError,
  RemoteSessionClientsUnlinkEMAMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRemoteSessionClientsUnlinkEMAMutation(client, options),
    ...options,
  });
}

export function mutationKeyRemoteSessionClientsUnlinkEMA(): MutationKey {
  return ["@gram/client", "remoteSessionClients", "unlinkEMA"];
}

export function buildRemoteSessionClientsUnlinkEMAMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RemoteSessionClientsUnlinkEMAMutationVariables,
  ) => Promise<RemoteSessionClientsUnlinkEMAMutationData>;
} {
  return {
    mutationKey: mutationKeyRemoteSessionClientsUnlinkEMA(),
    mutationFn: function remoteSessionClientsUnlinkEMAMutationFn({
      request,
      security,
      options,
    }): Promise<RemoteSessionClientsUnlinkEMAMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(remoteSessionClientsUnlinkEMA(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
